import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Phone, FileText, Building2, Home, MapPin, Clock, Award } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const LeasePlans = () => {
  const faqs = [
    {
      question: "What is a Lease Plan?",
      answer: "A lease plan is a scaled drawing showing the extent of a property being leased. It clearly defines the demised premises using edging colours, measurements and floor layouts. Required by HM Land Registry for lease registrations over 7 years."
    },
    {
      question: "When do I need a Lease Plan?",
      answer: "Lease plans are required for: new leases over 7 years being registered with Land Registry, lease extensions, lease variations affecting boundaries, and sometimes for commercial lettings where the landlord or tenant requires clarity on demised areas."
    },
    {
      question: "What does a compliant Lease Plan include?",
      answer: "A Land Registry compliant lease plan includes: a location plan showing the property in context, a floor plan with the demised premises edged in red, clear measurements or scale, north point, and annotations identifying the property address and floor level."
    },
    {
      question: "How much does a Lease Plan cost?",
      answer: "Lease plans typically cost from £150 for a simple flat or unit. Larger or more complex properties cost more. We provide fixed-price quotes based on your requirements. Volume discounts available for multiple units or developments."
    },
    {
      question: "How long does it take?",
      answer: "Standard turnaround is 3-5 working days from receiving all required information. Rush service (24-48 hours) available. We can also conduct measured surveys if existing drawings are unavailable."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Lease Plans | Land Registry Compliant | Residential & Commercial | Energy Performance Direct</title>
        <meta name="description" content="Professional lease plans for Land Registry compliance. Residential flats, commercial units, industrial premises. HM Land Registry compliant. From £150. Call 01392 980077." />
        <meta name="keywords" content="lease plans, land registry lease plan, commercial lease plan, residential lease plan, demise plan, leasehold plan, property lease drawing" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/lease-plans" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />

        <section className="py-16 bg-gradient-to-br from-indigo-50 via-blue-50 to-indigo-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block mb-4 px-4 py-2 bg-white rounded-full shadow-sm">
                <span className="text-sm font-medium text-indigo-600">Land Registry Compliant</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Lease Plans
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Professional lease plans for residential and commercial properties. HM Land Registry compliant drawings for lease registrations, extensions and variations.
              </p>
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <CheckCircle className="h-5 w-5 text-indigo-600" />
                  <span className="text-sm font-medium">From £150</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <CheckCircle className="h-5 w-5 text-indigo-600" />
                  <span className="text-sm font-medium">Land Registry Compliant</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <CheckCircle className="h-5 w-5 text-indigo-600" />
                  <span className="text-sm font-medium">3-5 Day Turnaround</span>
                </div>
              </div>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/booking">
                  <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700">
                    Order Lease Plan
                  </Button>
                </Link>
                <a href="tel:01392980077">
                  <Button size="lg" variant="outline" className="border-indigo-600 text-indigo-600 hover:bg-indigo-50">
                    <Phone className="mr-2 h-5 w-5" />
                    01392 980077
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">Lease Plan Services</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Home className="h-12 w-12 text-indigo-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Residential Lease Plans</h3>
                    <p className="text-gray-600">
                      Lease plans for flats, maisonettes, apartments and houses. New leases, extensions and variations. Land Registry compliant.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Building2 className="h-12 w-12 text-indigo-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Commercial Lease Plans</h3>
                    <p className="text-gray-600">
                      Lease plans for offices, retail units, warehouses and industrial premises. Clear demise identification for landlords and tenants.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <FileText className="h-12 w-12 text-indigo-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Development Lease Plans</h3>
                    <p className="text-gray-600">
                      Bulk lease plans for new developments. Volume pricing for multiple units. Consistent formatting across all plots.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <MapPin className="h-12 w-12 text-indigo-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Location Plans</h3>
                    <p className="text-gray-600">
                      OS-based location plans showing the property in its wider context. Site boundary clearly marked. Required for all lease registrations.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Clock className="h-12 w-12 text-indigo-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Rush Service</h3>
                    <p className="text-gray-600">
                      Urgent deadline? 24-48 hour turnaround available for time-critical lease completions. Contact us for availability.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Award className="h-12 w-12 text-indigo-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Measured Surveys</h3>
                    <p className="text-gray-600">
                      No existing drawings? We conduct measured surveys across the South West and produce lease plans from scratch.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">Regional Coverage</h2>
              <p className="text-center text-gray-600 mb-8">We provide lease plans across the South West and beyond. For properties in our core coverage area, we can also conduct measured surveys.</p>
              <div className="grid md:grid-cols-2 gap-4">
                <Link to="/lease-plans-west-country" className="block">
                  <Card className="hover:shadow-lg transition-shadow hover:border-indigo-300">
                    <CardContent className="pt-6">
                      <h3 className="font-semibold text-lg mb-2">West Country Lease Plans</h3>
                      <p className="text-gray-600">Devon, Cornwall, Somerset, Dorset, Bristol and surrounding areas.</p>
                    </CardContent>
                  </Card>
                </Link>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <h3 className="font-semibold text-lg mb-2">UK-Wide Service</h3>
                    <p className="text-gray-600">Remote lease plan production from your existing drawings, anywhere in the UK.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">Lease Plan FAQs</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <Card key={index}>
                    <CardContent className="pt-6">
                      <h3 className="font-semibold text-lg mb-2">{faq.question}</h3>
                      <p className="text-gray-600">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <p className="text-center mt-8 text-gray-600">
                Learn more about lease plan requirements in our <Link to="/resources/lease-plan-requirements" className="text-indigo-600 hover:underline">comprehensive guide</Link>.
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 bg-indigo-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Need a Lease Plan?</h2>
            <p className="text-xl mb-8 opacity-90">
              Professional, Land Registry compliant lease plans
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/booking">
                <Button size="lg" variant="secondary" className="bg-white text-indigo-600 hover:bg-gray-100">
                  Order Now
                </Button>
              </Link>
              <a href="tel:01392980077">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-indigo-700">
                  <Phone className="mr-2 h-5 w-5" />
                  01392 980077
                </Button>
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default LeasePlans;
